import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Stack, SvgIcon, Typography, alpha, useTheme } from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { Html5Qrcode } from 'html5-qrcode';
import vmIcon from '@/assets/vm-icon.png';

const iconPaths = {
  arrow: 'M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z',
  qr: 'M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm10-2h2v2h-2v-2zm4 0h2v4h-4v-2h2v-2zm-6 4h2v4h-2v-4zm4 2h4v2h-4v-2z',
};

function AppIcon({ path, ...props }: SvgIconProps & { path: string }) {
  return <SvgIcon {...props}><path d={path} /></SvgIcon>;
}

function extractEventPath(scanned: string): string | null {
  try {
    // Accept full URL or bare path like /events/XXXX
    const url = scanned.startsWith('http') ? new URL(scanned) : new URL(scanned, window.location.origin);
    const match = url.pathname.match(/^\/(events\/[A-Z0-9]+)/i);
    return match ? `/${match[1]}` : null;
  } catch {
    return null;
  }
}

type State = 'starting' | 'scanning' | 'success' | 'denied' | 'error';

export function ScanPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [state, setState] = useState<State>('starting');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const scanner = new Html5Qrcode('qr-viewport');
    scannerRef.current = scanner;
    let started = false;

    const stopScanner = () => {
      if (started && scanner.isScanning) {
        scanner.stop().catch(() => {});
      }
    };

    scanner
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1 },
        (decoded) => {
          const path = extractEventPath(decoded);
          if (!path) return;
          stopScanner();
          setState('success');
          setTimeout(() => navigate(path), 600);
        },
        () => {},
      )
      .then(() => {
        started = true;
        setState('scanning');
      })
      .catch((err: unknown) => {
        const msg = String(err).toLowerCase();
        if (msg.includes('permission') || msg.includes('notallowed')) {
          setState('denied');
        } else {
          setState('error');
          setErrorMsg(String(err));
        }
      });

    return () => stopScanner();
  }, [navigate]);

  const bg = `linear-gradient(120deg, ${alpha(theme.palette.primary.dark, 0.98)} 0%, ${alpha(theme.palette.text.primary, 0.95)} 52%, ${alpha(theme.palette.secondary.dark, 0.9)} 100%)`;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'primary.dark', backgroundImage: bg, color: 'common.white', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 }, pb: 1 }}>
        <Button
          onClick={() => navigate(-1)}
          startIcon={<AppIcon path={iconPaths.arrow} />}
          sx={{ color: alpha(theme.palette.common.white, 0.7), '&:hover': { color: 'common.white', bgcolor: alpha(theme.palette.common.white, 0.08) }, minWidth: 0, px: 1 }}
        >
          Back
        </Button>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 0.5 }}>
          <Box component="img" src={vmIcon} alt="" sx={{ width: 28, height: 28, borderRadius: 0.75 }} />
          <Typography sx={{ fontWeight: 900, fontSize: 16 }}>Vibe Masters</Typography>
        </Stack>
      </Stack>

      {/* Body */}
      <Stack flex={1} alignItems="center" justifyContent="center" spacing={3} sx={{ px: 2, pb: 4 }}>
        <Stack alignItems="center" spacing={0.75}>
          <Typography variant="h5" sx={{ fontWeight: 900, textAlign: 'center' }}>Scan event QR code</Typography>
          <Typography sx={{ fontSize: 14, color: alpha(theme.palette.common.white, 0.55), textAlign: 'center', maxWidth: 280 }}>
            Point your camera at the QR code at the venue to join the event.
          </Typography>
        </Stack>

        {/* Camera viewport — always rendered so html5-qrcode can attach to it */}
        <Box sx={{ position: 'relative', width: '100%', maxWidth: 340 }}>
          <Box
            id="qr-viewport"
            sx={{
              width: '100%',
              borderRadius: 2,
              overflow: 'hidden',
              border: `2px solid ${state === 'success' ? theme.palette.success.light : alpha(theme.palette.common.white, 0.18)}`,
              transition: 'border-color 0.3s',
              // hide the default html5-qrcode UI chrome
              '& #qr-shaded-region': { display: 'none' },
              '& video': { display: 'block', width: '100% !important', height: 'auto !important' },
            }}
          />

          {/* Overlay: scanning corners */}
          {state === 'scanning' && (
            <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map((corner) => (
                <Box
                  key={corner}
                  sx={{
                    position: 'absolute',
                    width: 28, height: 28,
                    borderColor: 'secondary.light',
                    borderStyle: 'solid',
                    borderWidth: 0,
                    ...(corner === 'top-left'     && { top: 10, left: 10, borderTopWidth: 3, borderLeftWidth: 3, borderTopLeftRadius: 4 }),
                    ...(corner === 'top-right'    && { top: 10, right: 10, borderTopWidth: 3, borderRightWidth: 3, borderTopRightRadius: 4 }),
                    ...(corner === 'bottom-left'  && { bottom: 10, left: 10, borderBottomWidth: 3, borderLeftWidth: 3, borderBottomLeftRadius: 4 }),
                    ...(corner === 'bottom-right' && { bottom: 10, right: 10, borderBottomWidth: 3, borderRightWidth: 3, borderBottomRightRadius: 4 }),
                  }}
                />
              ))}
            </Box>
          )}

          {/* Overlay: starting spinner */}
          {state === 'starting' && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.common.black, 0.5), borderRadius: 2 }}>
              <CircularProgress size={36} sx={{ color: 'secondary.light' }} />
            </Box>
          )}

          {/* Overlay: success flash */}
          {state === 'success' && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.success.main, 0.35), borderRadius: 2 }}>
              <Typography sx={{ fontSize: 40 }}>✓</Typography>
            </Box>
          )}
        </Box>

        {/* Status messages below camera */}
        {state === 'scanning' && (
          <Typography sx={{ fontSize: 13, color: alpha(theme.palette.common.white, 0.45), textAlign: 'center' }}>
            Scanning…
          </Typography>
        )}

        {state === 'denied' && (
          <Box sx={{ maxWidth: 320, p: 2.5, borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.error.light, 0.3)}`, bgcolor: alpha(theme.palette.error.main, 0.1), textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 800, color: 'error.light', mb: 0.75 }}>Camera access denied</Typography>
            <Typography sx={{ fontSize: 13, color: alpha(theme.palette.common.white, 0.55), lineHeight: 1.5 }}>
              Allow camera access in your browser settings, then reload the page.
            </Typography>
          </Box>
        )}

        {state === 'error' && (
          <Box sx={{ maxWidth: 320, p: 2.5, borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.error.light, 0.3)}`, bgcolor: alpha(theme.palette.error.main, 0.1), textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 800, color: 'error.light', mb: 0.75 }}>Camera unavailable</Typography>
            <Typography sx={{ fontSize: 13, color: alpha(theme.palette.common.white, 0.55) }}>{errorMsg || 'Could not access the camera.'}</Typography>
          </Box>
        )}

        {(state === 'denied' || state === 'error') && (
          <Button variant="outlined" onClick={() => window.location.reload()} sx={{ color: 'common.white', borderColor: alpha(theme.palette.common.white, 0.3), '&:hover': { borderColor: 'secondary.light', bgcolor: alpha(theme.palette.secondary.main, 0.14) } }}>
            Try again
          </Button>
        )}

        <Typography sx={{ fontSize: 12, color: alpha(theme.palette.common.white, 0.3), textAlign: 'center', maxWidth: 280 }}>
          <AppIcon path={iconPaths.qr} sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
          Works with the QR code displayed at the venue
        </Typography>
      </Stack>
    </Box>
  );
}
